import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Editor, EditorProps } from 'react-draft-wysiwyg';
import { ErrorBoundary } from 'react-error-boundary';

import { createStyles, Theme, WithStyles, withStyles } from '@material-ui/core';
import classnames from 'classnames';
import { ContentState, convertFromHTML, DraftDecorator, EditorState, Modifier } from 'draft-js';
import { escapeRegExp } from 'lodash';

import { usePrevious } from '@lib/hooks';
import useDebouncedCallback from '@lib/hooks/useDebouncedCallback';
import { PatientMessageTemplate } from '@lib/resources/types';

import { Squiggly } from './ListItemSquiggly';
import { Typography } from './Typography';

const styles = ({
  spacing: { unit },
  palette: { error },
  typography: { fontFamily },
  custom: {
    fontSizes: { body, tiny },
    palette: { colorSupportMidnight, colorAccentWatermelon, colorSupportSnow, colorLightBlue, colorDarkCyan },
  },
}: Theme) =>
  createStyles({
    root: {
      fontFamily,
      padding: '0px 16px',
      border: `1px solid ${colorLightBlue}`,
      // WORKAROUND extra margin for numbers on ordered lists getting cut off
      // https://app.clickup.com/t/57a3y3
      '& .DraftEditor-editorContainer': {
        marginLeft: unit / 2,
      },
      '& .public-DraftEditorPlaceholder-root': {
        marginLeft: unit / 2,
      },
    },
    rootIncludeToolbar: {
      padding: '16px 16px 0px 16px',
    },
    rootReadOnly: {
      padding: 0,
      marginTop: '-24px',
      border: 'none',
    },
    editor: {
      fontSize: body,
      color: colorSupportMidnight,
      minHeight: 140,
    },
    editorHtml: {
      fontSize: body,
      color: colorSupportMidnight,
      minHeight: 190,
    },
    validSquiggly: {
      borderRadius: 4,
      fontWeight: 400,
      backgroundColor: colorSupportSnow,
      color: colorDarkCyan,
    },
    invalidSquiggly: {
      borderBottom: `1px solid ${colorAccentWatermelon}`,
      color: colorAccentWatermelon,
      fontWeight: 400,
    },
    helperText: {
      color: error.main,
      fontSize: tiny,
      fontWeight: 500,
      paddingBottom: 16,
    },
  });

const callbackStrategy = (text: string, regexp: RegExp, callback: (start: number, end: number) => void) => {
  const matches = [...text.matchAll(regexp)];
  matches.forEach((match) => {
    const start = match.index || 0;
    const end = (match.index || 0) + match[0].length;
    callback(start, end);
  });
};

export interface BottomToolbarProps {
  handleTemplateSelected: (messageTemplate: PatientMessageTemplate) => void;
  handleSquigglySelected: (squiggly: Squiggly) => void;
}

/*
TODO: Handle HTML paste
https://msiebert.github.io/blog/draftjs-handling-pasted-content
https://stackoverflow.com/questions/50307326/how-to-insert-html-in-draft-js
*/

export interface TextEditorProps extends WithStyles<typeof styles>, Omit<EditorProps, 'editorState' | 'onChange'> {
  className?: React.HTMLAttributes<HTMLDivElement>['className'];
  variant?: 'default' | 'html';
  initialText?: string;
  autoFocus?: boolean;
  disabled?: boolean;
  squigglies?: string[];
  initialValue?: string;
  error?: boolean;
  helperText?: React.ReactNode;
  bottomToolbar: (renderProps: BottomToolbarProps) => JSX.Element | React.ReactNode;
  onContentChange?: (contentState: ContentState) => void;
}

const TextEditorComponent: React.FC<TextEditorProps> = ({
  classes,
  className,
  variant = 'default',
  autoFocus,
  squigglies = [],
  readOnly,
  disabled,
  bottomToolbar,
  initialText = '',
  error,
  helperText,
  onContentChange = () => {},
  ...rest
}) => {
  const editor = useRef<Editor>(null);
  const toolbarHidden = variant !== 'html' || readOnly;

  // Initialize editor state
  const [editorState, setEditorState] = useState(() => {
    if (initialText) {
      const { contentBlocks, entityMap } = convertFromHTML(initialText);
      return EditorState.createWithContent(ContentState.createFromBlockArray(contentBlocks, entityMap));
    }
    return EditorState.createEmpty();
  });

  const validSquigglyComponent = useMemo(
    () => ({ children }: { children: React.ReactNode }) => <span className={classes.validSquiggly}>{children}</span>,
    [classes.validSquiggly],
  );

  const invalidSquigglyComponent = useMemo(
    () => ({ children }: { children: React.ReactNode }) => <span className={classes.invalidSquiggly}>{children}</span>,
    [classes.invalidSquiggly],
  );

  // Decorators for squigglies
  const customDecorators = useMemo<DraftDecorator[]>(
    () => [
      {
        // decorator for dynamic squigglies
        strategy: (block, callback) => callbackStrategy(block.getText(), /{{custom-form-\w+}\}/gm, callback),
        component: validSquigglyComponent,
      },
      {
        // decorator for valid squigglies
        strategy: (block, callback) => {
          const text = block.getText();
          squigglies.forEach((word) => callbackStrategy(text, new RegExp(escapeRegExp(word), 'gm'), callback));
        },
        component: validSquigglyComponent,
      },
      {
        // decorator for invalid squigglies
        strategy: (block, callback) => callbackStrategy(block.getText(), /{{.*?}\}/gm, callback),
        component: invalidSquigglyComponent,
      },
    ],
    [squigglies, validSquigglyComponent, invalidSquigglyComponent],
  );

  // Debouncing callback since reading content from the editorState might be expensive. Eg `stateToHTML`
  const [debouncedOnContentChange] = useDebouncedCallback(onContentChange, 500);

  const handleChange = useCallback(
    (newEditorState: EditorState) => {
      setEditorState(newEditorState);
      // Method to update the state and dispatch `onContentChange` only in case it changed
      // Eg moving cursor also calls this method
      const currentContent = editorState.getCurrentContent();
      const newContent = newEditorState.getCurrentContent();
      if (currentContent !== newContent) {
        debouncedOnContentChange(newContent);
      }
    },
    [editorState, debouncedOnContentChange],
  );

  // Effect to clear formatting when changed from HTML to default
  const prevVariant = usePrevious(variant);
  useEffect(() => {
    if (variant === 'default' && prevVariant === 'html') {
      const text = editorState.getCurrentContent().getPlainText();
      const newEditorState = EditorState.push(editorState, ContentState.createFromText(text), 'remove-range');
      handleChange(newEditorState);
    }
  }, [variant, prevVariant, handleChange, editorState]);

  // Insert selected squiggly into the state
  const handleSquigglySelected = useCallback(
    (squiggly: Squiggly) => {
      const contentState = editorState.getCurrentContent();
      const selection = editorState.getSelection();
      const newContentState = Modifier.replaceText(contentState, selection, squiggly.id);
      const newEditorState = EditorState.push(editorState, newContentState, 'insert-characters');
      handleChange(newEditorState);
    },
    [editorState, handleChange],
  );

  // Insert selected template into the state
  const handleTemplateSelected = useCallback(
    (messageTemplate) => {
      const contentState = editorState.getCurrentContent();
      const selection = editorState.getSelection();
      const newContentState = Modifier.replaceText(contentState, selection, messageTemplate.message);
      const newEditorState = EditorState.push(editorState, newContentState, 'insert-characters');
      handleChange(newEditorState);
    },
    [editorState, handleChange],
  );

  const rootClasses = classnames(
    classes.root,
    {
      [classes.rootIncludeToolbar]: !toolbarHidden,
      [classes.rootReadOnly]: readOnly,
    },
    className,
  );

  return (
    <>
      <div className={rootClasses}>
        {/*
        TODO skip errorBoundary once we're sure https://app.clickup.com/t/676b38 is not a problem anymore
        */}
        <ErrorBoundary
          FallbackComponent={({ resetErrorBoundary }) => {
            resetErrorBoundary();
            return null;
          }}
        >
          <Editor
            ref={editor}
            editorState={editorState}
            toolbar={{
              options: ['inline', 'blockType', 'list', 'link', 'emoji', 'image', 'remove', 'history'],
              inline: {
                options: ['bold', 'italic', 'underline', 'strikethrough', 'monospace'],
              },
              blockType: {
                options: ['Normal', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'Blockquote'],
              },
            }}
            toolbarHidden={toolbarHidden}
            customDecorators={customDecorators}
            onEditorStateChange={handleChange}
            readOnly={disabled || readOnly}
            editorClassName={!toolbarHidden ? classes.editorHtml : classes.editor}
            {...rest}
          />
        </ErrorBoundary>
        {helperText && !(readOnly || disabled) && (
          <Typography className={classnames(classes.helperText)}>{helperText}</Typography>
        )}
      </div>
      {typeof bottomToolbar === 'function'
        ? bottomToolbar({ handleSquigglySelected, handleTemplateSelected })
        : bottomToolbar}
    </>
  );
};

export const TextEditor = withStyles(styles)(TextEditorComponent);
